import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ModalBase from "../ModalBase";
import SelectDropdown from "../../components/SelectDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faUser,
    faIdCard,
    faPlugCircleBolt,
} from "@fortawesome/pro-regular-svg-icons";
import { ModalInfoList, ModalInfoRow } from "./ModalInfoList";

const StartChargingModal = ({
    isOpen,
    setIsOpen,
    siteName,
    chargerName,
    connectorName,
    handleGetCustomers,
    selectedCustomer,
    setSelectedCustomer,
    handleGetTokens,
    existingTokens,
    onStartCharging,
}) => {
    const { t } = useTranslation();
    const [manualIdTag, setManualIdTag] = useState("");
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [localCustomers, setLocalCustomers] = useState([]);
    const [isLoadingTokens, setIsLoadingTokens] = useState(false);
    const [selectedToken, setSelectedToken] = useState("");

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setIsLoadingCustomers(true);
        setSelectedToken("");
        setManualIdTag("");
        setSelectedCustomer(null);

        (async () => {
            try {
                const list = await handleGetCustomers();
                if (!cancelled) setLocalCustomers(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error("❌ Failed to fetch customers:", err);
            } finally {
                if (!cancelled) setIsLoadingCustomers(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [isOpen, handleGetCustomers]);

    const handleCustomerSelect = async (selected) => {
        const customerId = selected ? selected.value : null;
        const customer = localCustomers.find((c) => c.customerPublicId === customerId);
        setSelectedCustomer(customer || null);
        setSelectedToken("");

        if (customer) {
            setIsLoadingTokens(true);
            try {
                await handleGetTokens(customer.customerPublicId);
            } finally {
                setIsLoadingTokens(false);
            }
        }
    };

    const handleStart = () => {
        const tagToken = selectedToken || manualIdTag;
        if (!tagToken) {
            alert(t("chargeMonitor.selectIdTag"));
            return;
        }
        onStartCharging(tagToken);
        setIsOpen(false);
    };

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
        menu: (base) => ({ ...base, zIndex: 2147483647 })
    };

    return (
        <ModalBase isOpen={isOpen} size="mdh"  onClose={() => setIsOpen(false)}>
            <div className="flex flex-col h-full"> 
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-sky-600 flex items-center gap-2">
                        <FontAwesomeIcon icon={faPlugCircleBolt} className="text-sky-500 text-lg" />
                        {t("chargeMonitor.startCharging")}
                    </h2>
                </div>

                <ModalInfoList className="border-b border-mute-100 pb-3 mb-4">
                    <ModalInfoRow label={t("entity.site")}>{siteName || "–"}</ModalInfoRow>
                    <ModalInfoRow label={t("entity.charger")}>{chargerName || "–"}</ModalInfoRow>
                    <ModalInfoRow label={t("entity.connector")}>{connectorName || "–"}</ModalInfoRow>
                </ModalInfoList>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-mute-600 mb-1 flex items-center gap-1">
                        <FontAwesomeIcon icon={faUser} className="text-sky-500 text-xs" />
                        {t("entity.customer")}
                    </label>
                    <SelectDropdown
                        isMulti={false}
                        isDisabled={isLoadingCustomers}
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        placeholder={
                            isLoadingCustomers
                                ? t("chargeMonitor.loadingCustomers")
                                : localCustomers.length === 0
                                    ? t("chargeMonitor.noCustomers")
                                    : t("chargeMonitor.selectCustomer")
                        }
                        options={
                            localCustomers.map((c) => ({
                                value: c.customerPublicId,
                                label: c.customerName || c.name || t("chargeMonitor.unnamedCustomer"),
                            })) || []
                        }
                        selectedValues={
                            selectedCustomer
                                ? [{
                                    value: selectedCustomer.customerPublicId,
                                    label:
                                        selectedCustomer.customerName ||
                                        selectedCustomer.name ||
                                        t("chargeMonitor.unnamedCustomer"),
                                }]
                                : []
                        }
                        onChange={handleCustomerSelect}
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-mute-600 mb-1 flex items-center gap-1">
                        <FontAwesomeIcon icon={faIdCard} className="text-amber-500 text-xs" />
                        {t("chargeMonitor.rfidToken")}
                    </label>
                    <SelectDropdown
                        isMulti={false}
                        isDisabled={isLoadingTokens}
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        placeholder={
                            isLoadingTokens
                                ? t("chargeMonitor.loadingTokens")
                                : existingTokens.length === 0
                                    ? t("chargeMonitor.noTokens")
                                    : t("chargeMonitor.selectToken")
                        }
                        options={
                            existingTokens.map((token) => ({
                                value: token.tagToken,
                                label: token.name || token.visualNumber || t("chargeMonitor.unnamedToken"),
                            })) || []
                        }
                        selectedValues={
                            selectedToken
                                ? [{
                                    value: selectedToken,
                                    label:
                                        existingTokens.find((token) => token.tagToken === selectedToken)?.name ??
                                        existingTokens.find((token) => token.tagToken === selectedToken)?.visualNumber ??
                                        selectedToken,
                                }]
                                : []
                        }
                        onChange={(selected) => setSelectedToken(selected?.value || "")}
                    />
                </div>

                <div className="mt-auto flex justify-end gap-3 pt-4 border-t border-mute-200 bg-mute-25">
                    <button className="btn btn-outline-primary" onClick={() => setIsOpen(false)}>
                        {t("common.cancel")}
                    </button>

                    <button className="btn-primary btn" onClick={handleStart} disabled={!manualIdTag && !selectedToken}>
                        <FontAwesomeIcon icon={faBolt} />
                        {t("chargeMonitor.startCharging")}
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};

export default StartChargingModal;
